import { commonHeader, generateEndpoint } from "~/config/apiConfig";
import { ErrorBoundary } from "~/errors/errorBoundary";
import type { UpdateRole, UpdateUser, User } from "~/interface/user";
import { type ApiResult, failure, success } from "./apiResult";

export class UserApi {
  private getRequestInfo(method: string, requestBody?: object): RequestInit {
    return {
      method: method,
      headers: commonHeader,
      credentials: "include",
      keepalive: true,
      ...{
        body: JSON.stringify(requestBody),
      },
    };
  }

  async get(organization_id: string): Promise<User | null> {
    const endpoint = generateEndpoint(`/organizations/${organization_id}/users/me`);
    const requestInfo = this.getRequestInfo("GET");

    try {
      const response = await fetch(endpoint, requestInfo);
      if (response.ok) {
        const user = await response.json();
        return user;
      } else {
        const errorMessage = ErrorBoundary(response.status);
        console.error(errorMessage);
        return null;
      }
    } catch (error) {
      console.error(error);
      return null;
    }
  }

  async update(organization_id: string, updateData: UpdateUser): Promise<Response | null> {
    const endpoint = generateEndpoint(`/organizations/${organization_id}/users/me`);
    const requestInfo = this.getRequestInfo("POST", updateData);

    try {
      const response = await fetch(endpoint, requestInfo);
      if (response.ok) {
        return response;
      } else {
        const errorMessage = ErrorBoundary(response.status);
        console.error(errorMessage);
        return null;
      }
    } catch (error) {
      console.error(error);
      return null;
    }
  }

  async updateRole(
    organization_id: string,
    target_user_id: string,
    updateRole: UpdateRole,
  ): Promise<boolean> {
    const endpoint = generateEndpoint(
      `/organizations/${organization_id}/users/${target_user_id}/role`,
    );
    const requestInfo = this.getRequestInfo("POST", updateRole);
    const response = await fetch(endpoint, requestInfo);

    try {
      if (response.ok) {
        return true;
      } else {
        const errorMessage = ErrorBoundary(response.status);
        console.error(errorMessage);
        return false;
      }
    } catch (error) {
      console.error(error);
      return false;
    }
  }

  async delete(organization_id: string, target_user_id: string): Promise<ApiResult<void>> {
    const endpoint = generateEndpoint(`/organizations/${organization_id}/users/${target_user_id}`);
    const requestInfo = this.getRequestInfo("DELETE");

    try {
      const response = await fetch(endpoint, requestInfo);
      if (response.ok) {
        return success(undefined);
      } else {
        let errorMessage: string;
        if (response.status >= 500) {
          errorMessage = ErrorBoundary(response.status);
        } else {
          try {
            const errorData = await response.json();
            const message = errorData.message as string;
            // 空文字列チェックを追加
            errorMessage = message?.trim() ? message : ErrorBoundary(response.status);
          } catch {
            errorMessage = ErrorBoundary(response.status);
          }
        }
        console.error(errorMessage);
        return failure(errorMessage);
      }
    } catch (error) {
      console.error(error);
      const errorMessage =
        error instanceof Error ? error.message : "ネットワークエラーが発生しました";
      return failure(errorMessage);
    }
  }
}
